import { z } from 'zod'
import { sendModeSchema } from './report'
import { issueFailureReasonSchema, issueOriginSchema, issueStatusSchema } from './report-issue'

/**
 * The kill switch (CLI-741, tech sync 2026-09-24: "non-negotiable for v0"). Two switches, one
 * per queue: `generation` halts the tick creating and building automatic issues, `delivery` halts
 * every send — scheduled, automatic, and the Send button — without cancelling anything. Flipping
 * a switch back on resumes: paused deliveries are re-scheduled by the next tick, paused builds are
 * queued by it. Stored in the app database so it takes effect on every instance at once, no
 * deploy, and read by the webhooks on every delivery.
 */
export const autoSendSwitchNameSchema = z.enum(['generation', 'delivery'])
export type AutoSendSwitchName = z.infer<typeof autoSendSwitchNameSchema>

export const autoSendSwitchSchema = z.object({
  paused: z.boolean(),
  reason: z.string().nullable(),
  updatedBy: z.string().nullable(),
  updatedAt: z.iso.datetime().nullable(),
})
export type AutoSendSwitch = z.infer<typeof autoSendSwitchSchema>

export const autoSendSwitchesSchema = z.object({
  generation: autoSendSwitchSchema,
  delivery: autoSendSwitchSchema,
})
export type AutoSendSwitches = z.infer<typeof autoSendSwitchesSchema>

export const setAutoSendSwitchSchema = z.object({
  paused: z.boolean(),
  /** Why — shown to everyone looking at the delivery queue while it is paused. */
  reason: z.string().max(500).optional(),
})
export type SetAutoSendSwitch = z.infer<typeof setAutoSendSwitchSchema>

/** One promise the canary judged, with the line a person reads when it fails. */
export const autoSendCanaryCheckSchema = z.object({
  name: z.string(),
  status: z.enum(['pass', 'fail']),
  detail: z.string(),
})
export type AutoSendCanaryCheck = z.infer<typeof autoSendCanaryCheckSchema>

/** The issue the canary judged: the one the tick created for the send moment under judgement. */
export const autoSendCanaryIssueSchema = z.object({
  id: z.uuid(),
  status: issueStatusSchema,
  origin: issueOriginSchema,
  issueDate: z.string(),
  targetSendAt: z.iso.datetime().nullable(),
  scheduledFor: z.iso.datetime().nullable(),
  sentAt: z.iso.datetime().nullable(),
  failedAt: z.iso.datetime().nullable(),
  failureReason: issueFailureReasonSchema.nullable(),
  lastError: z.string().nullable(),
  lateAlertedAt: z.iso.datetime().nullable(),
  generateAttempts: z.number().int().nonnegative(),
  sendAttempts: z.number().int().nonnegative(),
  keptStories: z.number().int().nonnegative(),
  /** The newest real send (mode `send`), or null when none has been attempted. */
  send: z
    .object({
      status: z.string(),
      sentAt: z.iso.datetime(),
      providerMessageId: z.string().nullable(),
      recipientCount: z.number().int().nonnegative(),
      error: z.string().nullable(),
      /**
       * Whether the send recorded the Google Doc it wrote (CLI-939). A yes/no, never the link: the
       * document is shared with anyone who has it.
       */
      wroteGoogleDoc: z.boolean(),
    })
    .nullable(),
})
export type AutoSendCanaryIssue = z.infer<typeof autoSendCanaryIssueSchema>

/** A canary report's configuration, in the fields the checks judge. */
export const autoSendCanaryReportSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  status: z.string(),
  sendMode: sendModeSchema,
  schedule: z.string(),
  timezone: z.string(),
  /** Every moment of the day it sends at: `sendTimes` plus multi-send's additional times. */
  sendTimes: z.array(z.string()),
  recipientCount: z.number().int().nonnegative(),
  recipientDomains: z.array(z.string()),
  /** Null is the standard layout. */
  templateId: z.uuid().nullable(),
  /** The template's name when it resolves; null when it does not (the send would fall back). */
  templateName: z.string().nullable(),
  /**
   * The Google Doc it delivers, when `exports.googleDoc` is on; null when it sends email only.
   * `templateName` and `tabCount` describe the Google Doc template as a send would load it: null
   * and 0 when it is unset or no longer resolves, which writes a one-tab document instead.
   */
  googleDoc: z
    .object({
      templateId: z.uuid().nullable(),
      templateName: z.string().nullable(),
      tabCount: z.number().int().nonnegative(),
    })
    .nullable(),
})
export type AutoSendCanaryReport = z.infer<typeof autoSendCanaryReportSchema>

/** One canary report, judged. `report` is null when the configured id names no report. */
export const autoSendCanaryResultSchema = z.object({
  reportId: z.uuid(),
  report: autoSendCanaryReportSchema.nullable(),
  /** The send moment under judgement: the newest one far enough in the past to have gone out. */
  moment: z.iso.datetime().nullable(),
  issue: autoSendCanaryIssueSchema.nullable(),
  ok: z.boolean(),
  checks: z.array(autoSendCanaryCheckSchema),
})
export type AutoSendCanaryResult = z.infer<typeof autoSendCanaryResultSchema>

/**
 * What `/api/internal/auto-send/canary` answers (behind the service token; CLI-919): every report
 * the environment names as a canary — real reports on automatic schedules, sending to an internal
 * address, so the real cron, QStash and SendGrid are exercised every hour — each judged against
 * its most recent send moment (apps/api/src/lib/auto-send-canary.ts). Recipients are a count and
 * their domains, never the addresses.
 */
export const autoSendCanaryResponseSchema = z.object({
  ok: z.boolean(),
  reports: z.array(autoSendCanaryResultSchema),
  now: z.iso.datetime(),
})
export type AutoSendCanaryResponse = z.infer<typeof autoSendCanaryResponseSchema>

/**
 * One canary report, BUILT on the revision that answered (CLI-947): the dry-run preview's pull
 * against the live data API and the mailer's render, nothing written. `name` is null when the
 * configured id names no report; `stories` is what the build kept, across every header.
 */
export const autoSendCanaryBuildResultSchema = z.object({
  reportId: z.uuid(),
  name: z.string().nullable(),
  ok: z.boolean(),
  stories: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative(),
  checks: z.array(autoSendCanaryCheckSchema),
})
export type AutoSendCanaryBuildResult = z.infer<typeof autoSendCanaryBuildResultSchema>

/**
 * What `GET /api/internal/auto-send/canary/build` answers (behind the service token): every
 * canary report, built now — what the deploy's smoke test asks a candidate before it takes
 * traffic (apps/api/src/lib/auto-send-canary-build.ts).
 */
export const autoSendCanaryBuildResponseSchema = z.object({
  ok: z.boolean(),
  reports: z.array(autoSendCanaryBuildResultSchema),
})
export type AutoSendCanaryBuildResponse = z.infer<typeof autoSendCanaryBuildResponseSchema>
